/**
 * Comprehensive Menu Customization Types
 * 
 * This file defines all types related to menu appearance and customization.
 * It provides a type-safe way to manage all visual aspects of the digital menu.
 */

// ============================================================================
// Color Types
// ============================================================================

export interface ColorScheme {
  /** Primary brand color - used for CTAs, highlights, active states */
  primary: string;
  
  /** Secondary brand color - used for accents and complements */
  secondary: string;
  
  /** Accent color - used for special highlights and notifications */
  accent: string;
  
  /** Main background color for the menu */
  background: string;
  
  /** Surface color - used for cards and elevated elements */
  surface: string;
  
  /** Primary text color */
  text: string;
  
  /** Secondary text color - for less important text */
  textSecondary: string;
  
  /** Border color for dividers and outlines */
  border: string;
  
  /** Error/warning color */
  error?: string;
  
  /** Success color */
  success?: string;
}

// ============================================================================
// Typography Types
// ============================================================================

export type FontScale = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type FontWeight = 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
export type FontFamily = 'inter' | 'roboto' | 'poppins' | 'playfair' | 'noto-sans-arabic' | 'genty-sans' | 'custom';

export interface TypographyConfig {
  /** Font family for the entire menu */
  fontFamily: FontFamily;
  
  /** Custom font family URL (if fontFamily is 'custom') */
  customFontUrl?: string;
  
  /** Overall font size scale */
  scale: FontScale;
  
  /** Weight for headings (category names, section titles) */
  headingWeight: FontWeight;
  
  /** Weight for body text (descriptions, prices) */
  bodyWeight: FontWeight;
  
  /** Enable/disable text shadows for better readability */
  textShadow: boolean;
  
  /** Line height multiplier */
  lineHeight: 'tight' | 'normal' | 'relaxed' | 'loose';
}

// ============================================================================
// Spacing Types
// ============================================================================

export type SpacingScale = 'compact' | 'normal' | 'relaxed' | 'loose';

export interface SpacingConfig {
  /** Gap between menu item cards */
  cardGap: SpacingScale;
  
  /** Internal padding of cards */
  cardPadding: SpacingScale;
  
  /** Spacing between sections (categories) */
  sectionSpacing: SpacingScale;
  
  /** Container padding (left/right margins) */
  containerPadding: SpacingScale;
}

// ============================================================================
// Visual Effects Types
// ============================================================================

export type BorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
export type Shadow = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type BorderWidth = 0 | 1 | 2 | 4;

export interface EffectsConfig {
  /** Border radius for cards and elements */
  borderRadius: BorderRadius;
  
  /** Shadow intensity for cards */
  shadow: Shadow;
  
  /** Border width for cards */
  borderWidth: BorderWidth;
  
  /** Enable hover effects on cards */
  hoverEffect: boolean;
  
  /** Animation/transition speed */
  transitionSpeed: 'fast' | 'normal' | 'slow';
  
  /** Glass morphism effect */
  glassMorphism: boolean;
}

// ============================================================================
// Layout Types
// ============================================================================

export type LayoutType = 'list' | 'grid' | 'card' | 'compact' | 'photo-grid' | 'masonry' | 'gallery';
export type MaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

export interface LayoutConfig {
  /** Menu layout type */
  type: LayoutType;
  
  /** Maximum width of the menu container */
  maxWidth: MaxWidth;
  
  /** Number of columns for grid layouts */
  gridColumns?: {
    mobile: 1 | 2 | 3;
    tablet: 1 | 2 | 3 | 4;
    desktop: 1 | 2 | 3 | 4 | 5 | 6;
  };
  
  /** Image aspect ratio for items */
  imageAspectRatio: 'square' | '4:3' | '16:9' | '3:2' | 'auto';
  
  /** Show/hide item images */
  showImages: boolean;
  
  /** Image position in card */
  imagePosition: 'top' | 'left' | 'right' | 'background';
}

// ============================================================================
// Background Types
// ============================================================================

export type BackgroundType = 'none' | 'color' | 'gradient' | 'image' | 'pattern';

export interface GradientConfig {
  type: 'linear' | 'radial';
  angle?: number; // For linear gradients
  colors: string[];
}

export interface BackgroundConfig {
  /** Type of background */
  type: BackgroundType;
  
  /** Background value (color hex, image URL, or gradient) */
  value: string;
  
  /** Gradient configuration (if type is 'gradient') */
  gradient?: GradientConfig;

  /** Pattern configuration (if type is 'pattern') */
  pattern?: {
    type: 'dots' | 'grid' | 'stripes' | 'waves' | 'geometric';
    color: string;
  };
  
  /** Overlay opacity (0-100) for better text readability */
  overlayOpacity: number;
  
  /** Blur the background */
  blur: boolean;
  
  /** Fixed or scrollable background */
  attachment: 'fixed' | 'scroll';
}

// ============================================================================
// Component-Specific Types
// ============================================================================

export type HeaderType = 'simple' | 'with-cover' | 'minimal' | 'centered' | 'split';
export type HeaderHeight = 'sm' | 'md' | 'lg' | 'xl';

export interface HeaderCustomization {
  /** Header layout type */
  type: HeaderType;
  
  /** Header height */
  height: HeaderHeight;
  
  /** Show/hide restaurant logo */
  showLogo: boolean;
  
  /** Logo size */
  logoSize: 'sm' | 'md' | 'lg';
  
  /** Show/hide restaurant description */
  showDescription: boolean;
  
  /** Show/hide cover photo */
  showCoverPhoto: boolean;
  
  /** Cover photo overlay opacity */
  coverPhotoOverlay: number;
  
  /** Sticky header on scroll */
  sticky: boolean;
}

export type CategoryTabsType = 'horizontal' | 'vertical' | 'icons-only' | 'pills' | 'minimal';
export type CategoryTabsSize = 'sm' | 'md' | 'lg';

export interface CategoryTabsCustomization {
  /** Category tabs layout */
  type: CategoryTabsType;
  
  /** Tab size */
  size: CategoryTabsSize;
  
  /** Show category icons/images */
  showIcons: boolean;
  
  /** Icon style */
  iconStyle: 'circle' | 'square' | 'rounded';
  
  /** Sticky tabs on scroll */
  sticky: boolean;
  
  /** Show item count in tabs */
  showItemCount: boolean;
  
  /** Scroll to category on click */
  scrollToCategory: boolean;
}

export interface MenuCardCustomization {
  /** Show/hide item description */
  showDescription: boolean;
  
  /** Truncate description after N lines */
  descriptionLines: 1 | 2 | 3 | 4;
  
  /** Show price badge or inline */
  priceStyle: 'badge' | 'inline' | 'large';
  
  /** Show availability status */
  showAvailability: boolean;
  
  /** Show ratings */
  showRatings: boolean;
  
  /** Show badges (featured, new, popular) */
  showBadges: boolean;
  
  /** Add to cart button style */
  addButtonStyle: 'icon' | 'text' | 'icon-text';
  
  /** Card elevation on hover */
  hoverElevation: boolean;
}

export type FloatingCartPosition = 'bottom-left' | 'bottom-right' | 'bottom-center' | 'top-right';
export type FloatingCartSize = 'sm' | 'md' | 'lg';

export interface FloatingCartCustomization {
  /** Floating cart position */
  position: FloatingCartPosition;
  
  /** Cart button size */
  size: FloatingCartSize;
  
  /** Show item count */
  showItemCount: boolean;
  
  /** Show total amount */
  showTotalAmount: boolean;
  
  /** Auto-hide on scroll down */
  autoHide: boolean;
  
  /** Animation style */
  animation: 'bounce' | 'pulse' | 'none';
  
  /** Button shape style */
  buttonStyle?: 'circle' | 'pill' | 'square';
  
  /** Custom button color override */
  buttonColor?: string;
  
  /** Custom icon color override */
  iconColor?: string;
}

// ============================================================================
// Main Customization Interface
// ============================================================================

export interface MenuCustomization {
  /** Color scheme */
  colors: ColorScheme;
  
  /** Typography configuration */
  typography: TypographyConfig;
  
  /** Spacing configuration */
  spacing: SpacingConfig;
  
  /** Visual effects */
  effects: EffectsConfig;
  
  /** Layout configuration */
  layout: LayoutConfig;
  
  /** Background configuration */
  background: BackgroundConfig;
  
  /** Component-specific customization */
  components: {
    header: HeaderCustomization;
    categoryTabs: CategoryTabsCustomization;
    menuCard: MenuCardCustomization;
    floatingCart: FloatingCartCustomization;
  };
}

// ============================================================================
// Preset Theme Types
// ============================================================================

export interface PresetTheme {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  customization: Partial<MenuCustomization>;
}

export type ThemeCategory = 'modern' | 'classic' | 'minimal' | 'bold' | 'elegant' | 'playful';

// ============================================================================
// Helper Types
// ============================================================================

/** Deep partial to allow partial theme overrides */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/** Theme override type */
export type ThemeOverride = DeepPartial<MenuCustomization>;

// ============================================================================
// Default Values (to be used in utils)
// ============================================================================

export const DEFAULT_COLOR_SCHEME: ColorScheme = {
  primary: '#D91F26',
  secondary: '#F28A2E',
  accent: '#F59E0B',
  background: '#F3F4F6',
  surface: '#FFFFFF',
  text: '#1F2937',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  error: '#EF4444',
  success: '#10B981',
};

export const DEFAULT_TYPOGRAPHY: TypographyConfig = {
  fontFamily: 'inter',
  scale: 'md',
  headingWeight: 'semibold',
  bodyWeight: 'normal',
  textShadow: false,
  lineHeight: 'normal',
};

export const DEFAULT_SPACING: SpacingConfig = {
  cardGap: 'normal',
  cardPadding: 'normal',
  sectionSpacing: 'normal',
  containerPadding: 'normal',
};

export const DEFAULT_EFFECTS: EffectsConfig = {
  borderRadius: 'lg',
  shadow: 'md',
  borderWidth: 0,
  hoverEffect: true,
  transitionSpeed: 'normal',
  glassMorphism: false,
};

export const DEFAULT_LAYOUT: LayoutConfig = {
  type: 'list',
  maxWidth: 'lg',
  gridColumns: {
    mobile: 1,
    tablet: 2,
    desktop: 3,
  },
  imageAspectRatio: '4:3',
  showImages: true,
  imagePosition: 'left',
};

export const DEFAULT_BACKGROUND: BackgroundConfig = {
  type: 'color',
  value: '#F3F4F6',
  overlayOpacity: 0,
  blur: false,
  attachment: 'scroll',
};
